import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  InfoOutlined as InfoIcon,
  PlayArrow as PlayIcon,
  Place as PlaceIcon,
  Search as SearchIcon,
  KeyboardArrowUp as UpIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { formatUtcInstantInTimeZone } from '@signals/app-shell/utils/time';
import type { PathfinderPttUtterance } from '../../services/pttUtterancesService';

export interface PttCaptionPanelProps {
  /** Every utterance loaded for the current entities/range (plus live tail). */
  utterances: PathfinderPttUtterance[];
  query: string;
  onQueryChange: (value: string) => void;
  activeId?: number | null;
  currentTime?: Date | null;
  /** Playback window from the time slider; null when the slider shows the full extent. */
  playbackWindow?: { start: Date; end: Date } | null;
  /** Track colours keyed by entity/device id so a sender matches their line on the map. */
  senderColors?: Record<string, string>;
  timeZone?: string;
  /** Live tail status; undefined hides the badge. */
  live?: { connected: boolean };
  /** Click a line: fly the map to the transmit location and scrub the slider. */
  onSelect?: (utterance: PathfinderPttUtterance) => void;
  /** Scrub to the line and start playback. */
  onPlayFrom?: (utterance: PathfinderPttUtterance) => void;
}

const GROUP_GAP_MS = 60_000;
const DIVIDER_GAP_MS = 60 * 60_000;
interface CaptionGroup {
  key: string;
  sender: string;
  team: string;
  color: string;
  startMs: number;
  endMs: number;
  lines: PathfinderPttUtterance[];
}

function senderLabel(u: PathfinderPttUtterance): string {
  return u.sender || u.entity_id || u.device_id || 'Unknown';
}

function initials(name: string): string {
  const parts = name.replace(/[-_]+/g, ' ').trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function hashColor(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  return PTT_SENDER_PALETTE[Math.abs(h) % PTT_SENDER_PALETTE.length];
}

/**
 * PTT-specific palette. Track colours are assigned per *device type* (every
 * phone is green), so tinting bubbles with them makes every sender identical
 * and invisible against the track. These hues are chosen to sit apart from the
 * green/blue track defaults and the red "current position" marker.
 */
export const PTT_SENDER_PALETTE = [
  '#ffb300', // amber
  '#f06292', // pink
  '#4fc3f7', // light blue
  '#ba68c8', // purple
  '#ff7043', // deep orange
  '#fff176', // yellow
  '#80deea', // cyan
  '#7986cb', // indigo
  '#a1887f', // brown
  '#e0e0e0', // grey
];

/**
 * Build a stable sender → colour map for the loaded utterances. Senders are
 * sorted so the same roster always gets the same colours, and every sender in
 * the roster is guaranteed a distinct hue until the palette wraps.
 */
export function buildPttSenderColors(utterances: PathfinderPttUtterance[]): Record<string, string> {
  const senders = Array.from(new Set(utterances.map(senderLabel))).sort((a, b) => a.localeCompare(b));
  const out: Record<string, string> = {};
  senders.forEach((s, i) => {
    out[s] = PTT_SENDER_PALETTE[i % PTT_SENDER_PALETTE.length];
  });
  return out;
}

/** Resolve a sender to its PTT colour; falls back to a hash of the sender name. */
export function pttSenderColor(u: PathfinderPttUtterance, colors?: Record<string, string>): string {
  const ent = u.entity_id || '';
  const candidates = [senderLabel(u), ent, u.device_id].filter(Boolean) as string[];
  if (colors) {
    for (const k of candidates) {
      if (colors[k]) return colors[k];
    }
  }
  return hashColor(senderLabel(u));
}

function overlapsWindow(u: PathfinderPttUtterance, w: { start: Date; end: Date }): boolean {
  const a = new Date(u.started_at).getTime();
  const b = new Date(u.ended_at || u.started_at).getTime();
  return a <= w.end.getTime() && b >= w.start.getTime();
}

const PttCaptionPanel: React.FC<PttCaptionPanelProps> = ({
  utterances,
  query,
  onQueryChange,
  activeId = null,
  playbackWindow = null,
  senderColors,
  timeZone,
  live,
  onSelect,
  onPlayFrom,
}: PttCaptionPanelProps) => {
  const tz = timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  const listRef = useRef<HTMLDivElement | null>(null);
  const activeRef = useRef<HTMLDivElement | null>(null);

  const [teamFilter, setTeamFilter] = useState<Set<string>>(new Set());
  const [senderFilter, setSenderFilter] = useState<Set<string>>(new Set());
  const [followWindow, setFollowWindow] = useState(true);
  const [locatedOnly, setLocatedOnly] = useState(false);
  const [showEmpty, setShowEmpty] = useState(false);

  // Live tail: newest is on top; count arrivals while the user is scrolled away from it.
  const [atTop, setAtTop] = useState(true);
  const [pendingNew, setPendingNew] = useState(0);
  const prevCountRef = useRef(utterances.length);

  const fmtTime = useCallback(
    (iso: string) => formatUtcInstantInTimeZone(iso, tz, { hour: '2-digit', minute: '2-digit' }),
    [tz],
  );
  const fmtDay = useCallback(
    (iso: string) => formatUtcInstantInTimeZone(iso, tz, { weekday: 'short', month: 'short', day: 'numeric' }),
    [tz],
  );

  const sorted = useMemo(
    () =>
      [...utterances].sort((a, b) => {
        const byTime = new Date(b.started_at).getTime() - new Date(a.started_at).getTime();
        return byTime !== 0 ? byTime : b.id - a.id;
      }),
    [utterances],
  );

  const teams = useMemo(
    () => Array.from(new Set(sorted.map((u) => u.team).filter((t): t is string => Boolean(t)))).sort(),
    [sorted],
  );
  const senders = useMemo(
    () => Array.from(new Set(sorted.map(senderLabel))).sort(),
    [sorted],
  );

  // Filters other than the window apply first so the earlier/later counts are honest.
  const prefiltered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return sorted.filter((u) => {
      if (teamFilter.size && !(u.team && teamFilter.has(u.team))) return false;
      if (senderFilter.size && !senderFilter.has(senderLabel(u))) return false;
      if (locatedOnly && (u.latitude == null || u.longitude == null)) return false;
      if (!showEmpty && !(u.transcript || '').trim()) return false;
      if (needle && !(u.transcript || '').toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [sorted, teamFilter, senderFilter, locatedOnly, showEmpty, query]);

  const windowed = followWindow && playbackWindow != null;
  const { visible, earlier, later } = useMemo(() => {
    if (!windowed || !playbackWindow) return { visible: prefiltered, earlier: 0, later: 0 };
    const startMs = playbackWindow.start.getTime();
    const endMs = playbackWindow.end.getTime();
    let earlierCount = 0;
    let laterCount = 0;
    const vis = prefiltered.filter((u) => {
      if (overlapsWindow(u, playbackWindow)) return true;
      if (new Date(u.started_at).getTime() < startMs) earlierCount++;
      else if (new Date(u.started_at).getTime() > endMs) laterCount++;
      return false;
    });
    return { visible: vis, earlier: earlierCount, later: laterCount };
  }, [prefiltered, windowed, playbackWindow]);

  const emptyCount = useMemo(
    () => sorted.filter((u) => !(u.transcript || '').trim()).length,
    [sorted],
  );

  // Collapse consecutive lines from the same sender within a minute into one card.
  const groups = useMemo<CaptionGroup[]>(() => {
    const out: CaptionGroup[] = [];
    for (const u of visible) {
      const sender = senderLabel(u);
      const startMs = new Date(u.started_at).getTime();
      const endMs = new Date(u.ended_at || u.started_at).getTime();
      const last = out[out.length - 1];
      // Walk is newest-first, so a burst continues when this older line ends
      // within a minute of the group's earliest start.
      if (last && last.sender === sender && last.startMs - endMs <= GROUP_GAP_MS) {
        last.lines.push(u);
        last.startMs = Math.min(last.startMs, startMs);
        last.endMs = Math.max(last.endMs, endMs);
      } else {
        out.push({
          key: `${sender}-${u.id}`,
          sender,
          team: u.team || '',
          color: pttSenderColor(u, senderColors),
          startMs,
          endMs,
          lines: [u],
        });
      }
    }
    return out;
  }, [visible, senderColors]);

  // Keep the live line in view during playback.
  useEffect(() => {
    if (!activeRef.current) return;
    activeRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [activeId]);

  // Live tail bookkeeping.
  useEffect(() => {
    const prev = prevCountRef.current;
    prevCountRef.current = utterances.length;
    if (!live?.connected || utterances.length <= prev) return;
    if (atTop) {
      requestAnimationFrame(() => {
        if (listRef.current) listRef.current.scrollTop = 0;
      });
    } else {
      setPendingNew((n) => n + (utterances.length - prev));
    }
  }, [utterances.length, live?.connected, atTop]);

  const handleScroll = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    const nearTop = el.scrollTop < 40;
    setAtTop(nearTop);
    if (nearTop) setPendingNew(0);
  }, []);

  const jumpToTop = useCallback(() => {
    const el = listRef.current;
    if (el) el.scrollTo({ top: 0, behavior: 'smooth' });
    setPendingNew(0);
  }, []);

  const toggleIn = (set: Set<string>, value: string, setter: (s: Set<string>) => void) => {
    const next = new Set(set);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    setter(next);
  };

  const filtersActive = teamFilter.size > 0 || senderFilter.size > 0 || locatedOnly || query.trim().length > 0;
  const clearFilters = () => {
    setTeamFilter(new Set());
    setSenderFilter(new Set());
    setLocatedOnly(false);
    onQueryChange('');
  };

  const total = sorted.length;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 360, height: '100%' }}>
      {/* Header: count, live badge, help */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="subtitle2" sx={{ flex: 1 }}>
          Radio transcript
          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            {visible.length === total ? total : `${visible.length} of ${total}`}
          </Typography>
        </Typography>
        {live && (
          <Chip
            size="small"
            label={live.connected ? 'LIVE' : 'OFFLINE'}
            color={live.connected ? 'error' : 'default'}
            variant={live.connected ? 'filled' : 'outlined'}
            sx={{ height: 20, fontSize: 10, fontWeight: 700, letterSpacing: 0.5 }}
          />
        )}
        <Tooltip
          title="PTT transmissions for the loaded entities and co-travelers. The live line highlights during playback. Click a line to fly to where it was sent and move the timeline there; press ▶ to play from it."
        >
          <InfoIcon fontSize="small" color="disabled" />
        </Tooltip>
      </Stack>

      {/* Search */}
      <TextField
        size="small"
        placeholder="Search captions"
        value={query}
        onChange={(e) => onQueryChange(e.target.value)}
        fullWidth
        sx={{ mb: 1 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: query ? (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => onQueryChange('')} aria-label="Clear search">
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : undefined,
        }}
      />

      {/* Team / sender chips */}
      {(teams.length > 1 || senders.length > 1) && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
          {teams.length > 1 &&
            teams.map((t) => {
              const on = teamFilter.has(t);
              const color = hashColor(t);
              return (
                <Chip
                  key={`team-${t}`}
                  size="small"
                  label={t}
                  onClick={() => toggleIn(teamFilter, t, setTeamFilter)}
                  variant={on ? 'filled' : 'outlined'}
                  sx={{
                    height: 22,
                    fontSize: 11,
                    borderColor: color,
                    bgcolor: on ? color : 'transparent',
                    color: on ? '#000' : 'text.primary',
                    '&:hover': { bgcolor: on ? color : 'action.hover' },
                  }}
                />
              );
            })}
          {senders.length > 1 &&
            senders.map((s) => {
              const on = senderFilter.has(s);
              const sample = sorted.find((u) => senderLabel(u) === s);
              const color = sample ? pttSenderColor(sample, senderColors) : hashColor(s);
              return (
                <Chip
                  key={`sender-${s}`}
                  size="small"
                  avatar={<Avatar sx={{ bgcolor: color, color: '#000', fontSize: 9, width: 18, height: 18 }}>{initials(s)}</Avatar>}
                  label={s}
                  onClick={() => toggleIn(senderFilter, s, setSenderFilter)}
                  variant={on ? 'filled' : 'outlined'}
                  sx={{ height: 22, fontSize: 11 }}
                />
              );
            })}
        </Box>
      )}

      {/* Toggles */}
      <Stack direction="row" alignItems="center" flexWrap="wrap" sx={{ mb: 1, columnGap: 1 }}>
        <FormControlLabel
          sx={{ m: 0 }}
          control={
            <Switch
              size="small"
              checked={windowed}
              disabled={playbackWindow == null}
              onChange={(e) => setFollowWindow(e.target.checked)}
            />
          }
          label={
            <Typography variant="caption">
              {playbackWindow == null ? 'Full extent' : 'Follow window'}
            </Typography>
          }
        />
        <FormControlLabel
          sx={{ m: 0 }}
          control={<Switch size="small" checked={locatedOnly} onChange={(e) => setLocatedOnly(e.target.checked)} />}
          label={<Typography variant="caption">Located only</Typography>}
        />
        {emptyCount > 0 && (
          <FormControlLabel
            sx={{ m: 0 }}
            control={<Switch size="small" checked={showEmpty} onChange={(e) => setShowEmpty(e.target.checked)} />}
            label={<Typography variant="caption">Show empty ({emptyCount})</Typography>}
          />
        )}
        {filtersActive && (
          <Button size="small" onClick={clearFilters} sx={{ ml: 'auto', minWidth: 0, fontSize: 11 }}>
            Clear
          </Button>
        )}
      </Stack>

      {/* Newer transmissions sit above this window. */}
      {windowed && later > 0 && (
        <Button size="small" onClick={() => setFollowWindow(false)} sx={{ alignSelf: 'center', fontSize: 11, mb: 0.5 }}>
          ▲ {later} later — show all
        </Button>
      )}

      {/* List */}
      {total === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No transcripts. Load entities for a time range to see their PTT traffic.
        </Typography>
      ) : visible.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {windowed ? 'No transmissions in the current window.' : 'Nothing matches the current filters.'}
        </Typography>
      ) : (
        <Box sx={{ position: 'relative', flex: 1, minHeight: 240 }}>
          <Box ref={listRef} onScroll={handleScroll} sx={{ position: 'absolute', inset: 0, overflowY: 'auto', pr: 0.5 }}>
            {groups.map((g, gi) => {
              const prev = groups[gi - 1];
              const first = g.lines[0];
              const needDivider =
                !prev ||
                prev.startMs - g.endMs > DIVIDER_GAP_MS ||
                fmtDay(first.started_at) !== fmtDay(prev.lines[0].started_at);
              return (
                <React.Fragment key={g.key}>
                  {needDivider && (
                    <Divider sx={{ my: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {fmtDay(first.started_at)} · {fmtTime(first.started_at)}
                      </Typography>
                    </Divider>
                  )}
                  <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <Avatar
                      sx={{ width: 28, height: 28, fontSize: 11, fontWeight: 700, bgcolor: g.color, color: '#000', mt: 0.25 }}
                      title={g.sender}
                    >
                      {initials(g.sender)}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0, borderLeft: 3, borderColor: g.color, pl: 1 }}>
                      <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, color: 'text.secondary', mb: 0.25 }}>
                        {g.sender}
                        {g.team ? ` · ${g.team}` : ''}
                      </Typography>
                      {g.lines.map((u) => {
                        const active = u.id === activeId;
                        const located = u.latitude != null && u.longitude != null;
                        return (
                          <Box
                            key={u.id}
                            ref={active ? activeRef : undefined}
                            onClick={() => onSelect?.(u)}
                            sx={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 0.75,
                              px: 0.75,
                              py: 0.5,
                              mb: 0.25,
                              borderRadius: 1,
                              cursor: 'pointer',
                              bgcolor: active ? 'action.selected' : 'transparent',
                              outline: active ? `1px solid ${g.color}` : 'none',
                              '&:hover': { bgcolor: 'action.hover' },
                              '&:hover .ptt-play': { opacity: 1 },
                              transition: 'background-color 0.15s',
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: active ? 'text.primary' : 'text.disabled', fontVariantNumeric: 'tabular-nums', flexShrink: 0, pt: '2px' }}
                            >
                              {fmtTime(u.started_at)}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                flex: 1,
                                whiteSpace: 'pre-wrap',
                                fontWeight: active ? 600 : 400,
                                color: active ? 'text.primary' : (u.transcript || '').trim() ? 'text.primary' : 'text.disabled',
                                fontStyle: (u.transcript || '').trim() ? 'normal' : 'italic',
                              }}
                            >
                              {(u.transcript || '').trim() || '(no transcript)'}
                            </Typography>
                            {located && <PlaceIcon sx={{ fontSize: 14, color: 'text.disabled', mt: '3px' }} />}
                            {onPlayFrom && (
                              <IconButton
                                className="ptt-play"
                                size="small"
                                aria-label="Play from here"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onPlayFrom(u);
                                }}
                                sx={{ p: 0.25, opacity: active ? 1 : 0, transition: 'opacity 0.15s' }}
                              >
                                <PlayIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                </React.Fragment>
              );
            })}
          </Box>
          {pendingNew > 0 && (
            <Chip
              icon={<UpIcon />}
              label={`${pendingNew} new`}
              color="primary"
              size="small"
              onClick={jumpToTop}
              sx={{ position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)', boxShadow: 3 }}
            />
          )}
        </Box>
      )}

      {/* Older transmissions sit below this window. */}
      {windowed && earlier > 0 && (
        <Button size="small" onClick={() => setFollowWindow(false)} sx={{ alignSelf: 'center', fontSize: 11, mt: 0.5 }}>
          ▼ {earlier} earlier — show all
        </Button>
      )}
    </Box>
  );
};

export default PttCaptionPanel;
